/**
 * `useConfirmModal` — a confirmação e o aviso das páginas do Logigate e da Escola, em Tailwind
 * (com as peças do kit `@/Logigate/ui`). Era feito com o `modals` do Mantine; a API ficou igual:
 *
 * ```tsx
 * const { show, hide } = useConfirmModal();
 * show("Tens a certeza que queres apagar?", () => apagar(), { tipo: "question" }, "confirm");
 * show("Gravado com sucesso!"); // aviso só com «Ok» (modo "modal", tipo "success")
 * ```
 *
 * - `confirm`: «Sim» chama `onConfirm` e fecha; «Não», Esc ou um toque fora só fecham. O foco
 *   começa no «Não» (como no Mantine, onde era o primeiro botão): um Enter distraído não apaga nada.
 * - `modal`: o aviso com um botão («Ok»). O `onConfirm` é ignorado, como antes.
 * - Várias chamadas seguidas empilham-se (a de cima é a que responde ao Esc); `hide()` fecha todas.
 *
 * Não precisa do `ModalsProvider` do Mantine: desenha-se num portal no `<body>`, por cima das
 * janelas do kit (`z-[200]`) e dos `Modal` do Mantine, e por baixo dos avisos do `Pops`.
 * Código novo usa o `Pops` (`@/utils/Pops`); isto fica para as páginas que já o usavam.
 */
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { Botao, useSaida } from "@/Logigate/ui";
import { cn } from "@/lib/utils";

interface ConfirmModalContextProps {
    children: ReactNode;
}

type ModoConfirmacao = "confirm" | "modal";

interface ConfirmModalContextType {
    show: (message: string, onConfirm?: (() => void) | null, options?: ConfirmModalOptions | null, mode?: ModoConfirmacao) => void;
    hide: () => void;
}

interface ConfirmModalOptions {
    // Os três são opcionais: o `show` já tem omissão para cada um, e quem só quer mudar o ícone
    // passava `{tipo: "question"}` — que o tipo recusava.
    tipo?: "danger" | "success" | "info" | "question";
    btnCancel?: string;
    btnOk?: string;
}

type TipoConfirmacao = NonNullable<ConfirmModalOptions["tipo"]>;

const ConfirmModalContext = createContext<ConfirmModalContextType | undefined>(undefined);

/** O ícone e a cor de cada tipo (os mesmos do Mantine: verde, vermelho, laranja e azul). */
const ICON_MAP: Record<TipoConfirmacao, { icon: string; fundo: string }> = {
    success: { icon: "material-symbols:check-circle-rounded", fundo: "bg-green-600 dark:bg-green-500" },
    danger: { icon: "material-symbols:error-rounded", fundo: "bg-red-600 dark:bg-red-500" },
    info: { icon: "material-symbols:warning-rounded", fundo: "bg-orange-500 dark:bg-orange-500" },
    question: { icon: "material-symbols:help-rounded", fundo: "bg-blue-600 dark:bg-blue-500" },
};

/** A cor do botão principal (`cor` do `Botao` do kit). */
const COLOR_MAP: Record<TipoConfirmacao, string> = {
    danger: "red",
    success: "green",
    info: "orange",
    question: "blue",
};

const TITLE_MAP: Record<TipoConfirmacao, string> = {
    success: "Sucesso",
    danger: "Erro",
    info: "Aviso",
    question: "Confirmação",
};

interface Dialogo {
    id: number;
    mensagem: ReactNode;
    onConfirm?: (() => void) | null;
    tipo: TipoConfirmacao;
    modo: ModoConfirmacao;
    btnOk?: string;
    btnCancel?: string;
    aberta: boolean;
}

let proximoId = 1;

/** Quantas confirmações estão abertas (para saber quando devolver o scroll à página). */
let abertas = 0;
/** Se fomos nós a tirar o scroll à página (uma janela do kit aberta por baixo já o tinha tirado). */
let tiramosOScroll = false;

function haAvisoAberto(): boolean {
    return document.body.classList.contains("swal2-shown");
}

const SELECTOR_FOCAVEL = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Um `Modal` do Mantine aberto por baixo fecha com o Esc (ouve a janela, antes de nós), a não ser
 * que a tecla venha de um elemento com este atributo: o Esc da confirmação só fecha a confirmação.
 */
const PARAR_ESC_DO_MANTINE = { "data-mantine-stop-propagation": "true" } as const;

/** Uma confirmação: entra e sai com animação, e sai da lista depois de acabar de sair. */
function DialogoConfirmacao({ dialogo, onFechar, onConfirmar, onSaiu }: {
    dialogo: Dialogo;
    onFechar: (id: number) => void;
    onConfirmar: (id: number) => void;
    onSaiu: (id: number) => void;
}) {
    const { id, aberta, tipo, modo, mensagem } = dialogo;
    const { montada, aSair } = useSaida(aberta);
    const camada = useRef<HTMLDivElement>(null);
    const caixa = useRef<HTMLDivElement>(null);
    const botaoInicial = useRef<HTMLButtonElement>(null);
    const idTitulo = useId();
    const idTexto = useId();
    const icone = ICON_MAP[tipo] ?? ICON_MAP.success;

    // Acabou de sair: tira-se da lista.
    useEffect(() => {
        if (!montada) {
            onSaiu(id);
        }
    }, [montada, id, onSaiu]);

    // O scroll da página, o foco inicial e o regresso do foco a quem abriu.
    useEffect(() => {
        if (!aberta) {
            return;
        }
        let antes = document.activeElement as HTMLElement | null;
        const html = document.documentElement;
        if (abertas === 0 && html.style.overflow !== "hidden") {
            const barra = window.innerWidth - html.clientWidth;
            html.style.overflow = "hidden";
            if (barra > 0) {
                html.style.paddingRight = `${barra}px`;
            }
            tiramosOScroll = true;
        }
        abertas += 1;

        // Depois dos 10 ms em que um `Menu` do Mantine devolve o foco ao botão que o abriu (o
        // «Eliminar» de uma linha costuma vir de lá): senão o foco ia parar ao «…» da linha.
        const t = window.setTimeout(() => {
            // Quem tem o foco agora (o «…» a que o menu o devolveu) é quem o recebe de volta ao fechar.
            const focado = document.activeElement as HTMLElement | null;
            if (focado && focado !== document.body && !camada.current?.contains(focado)) {
                antes = focado;
            }
            botaoInicial.current?.focus({ preventScroll: true });
        }, 50);

        return () => {
            window.clearTimeout(t);
            abertas = Math.max(0, abertas - 1);
            if (abertas === 0 && tiramosOScroll) {
                tiramosOScroll = false;
                // Uma janela do kit aberta entretanto trata do scroll ela própria.
                if (!document.querySelector("[data-janela-logigate]")) {
                    html.style.overflow = "";
                    html.style.paddingRight = "";
                }
            }
            // Um aviso do `Pops` aberto pelo `onConfirm` fica com o foco.
            if (antes && document.contains(antes) && !haAvisoAberto()) {
                antes.focus({ preventScroll: true });
            }
        };
    }, [aberta]);

    // Esc fecha a de cima (sem fazer nada), a não ser que haja um aviso do Pops por cima.
    useEffect(() => {
        if (!aberta) {
            return;
        }
        const tecla = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || haAvisoAberto()) {
                return;
            }
            // A de cima que ainda não está a sair.
            const todas = document.querySelectorAll("[data-sobreposicao-logigate]:not([data-a-fechar])");
            if (todas[todas.length - 1] !== camada.current) {
                return;
            }
            e.stopPropagation();
            e.preventDefault();
            onFechar(id);
        };
        document.addEventListener("keydown", tecla);
        return () => document.removeEventListener("keydown", tecla);
    }, [aberta, id, onFechar]);

    if (!montada) {
        return null;
    }

    // O Tab não sai da caixa.
    const prenderTab = (e: React.KeyboardEvent) => {
        if (e.key !== "Tab" || !caixa.current) {
            return;
        }
        const focaveis = Array.from(caixa.current.querySelectorAll<HTMLElement>(SELECTOR_FOCAVEL));
        if (focaveis.length === 0) {
            return;
        }
        const primeiro = focaveis[0];
        const ultimo = focaveis[focaveis.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) {
            e.preventDefault();
            ultimo.focus();
        } else if (!e.shiftKey && document.activeElement === ultimo) {
            e.preventDefault();
            primeiro.focus();
        } else if (!caixa.current.contains(document.activeElement)) {
            e.preventDefault();
            primeiro.focus();
        }
    };

    const tocarFora = (e: React.MouseEvent<HTMLDivElement>) => {
        if (aSair || haAvisoAberto() || caixa.current?.contains(e.target as Node)) {
            return;
        }
        onFechar(id);
    };

    return (
        <div
            ref={camada}
            data-sobreposicao-logigate
            data-confirmacao-logigate
            data-a-fechar={aSair ? "" : undefined}
            aria-hidden={aSair || undefined}
            className={cn("fixed inset-0 z-[250]", aSair && "pointer-events-none")}
            onKeyDown={prenderTab}
        >
            <div
                className={cn(
                    "fixed inset-0 bg-zinc-950/45 backdrop-blur-[2px]",
                    aSair ? "animate-out fade-out-0 fill-mode-forwards duration-150" : "animate-in fade-in-0",
                )}
                aria-hidden
            />
            <div className="scroll-bonito fixed inset-0 overflow-y-auto overscroll-contain" onMouseDown={tocarFora}>
                <div className="flex min-h-full items-center justify-center p-4">
                    <div
                        ref={caixa}
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby={idTitulo}
                        aria-describedby={idTexto}
                        className={cn(
                            "relative w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-6 text-center shadow-2xl",
                            "dark:border-zinc-800 dark:bg-zinc-900",
                            aSair ? "animate-out fade-out-0 zoom-out-95 fill-mode-forwards duration-150" : "animate-in fade-in-0 zoom-in-95",
                        )}
                    >
                        <span className={cn("mx-auto flex size-14 items-center justify-center rounded-full text-white shadow-sm", icone.fundo)}>
                            <Icon icon={icone.icon} width={32} height={32} aria-hidden />
                        </span>
                        <h2 id={idTitulo} className="mt-4 text-lg font-bold text-zinc-900 dark:text-zinc-50">
                            {TITLE_MAP[tipo] ?? TITLE_MAP.success}
                        </h2>
                        <div id={idTexto} className="mt-1.5 whitespace-pre-line break-words text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                            {mensagem}
                        </div>

                        {modo === "confirm" ? (
                            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
                                <Botao ref={botaoInicial} {...PARAR_ESC_DO_MANTINE} variante="neutro" className="h-11 sm:h-9 sm:min-w-24" onClick={() => onFechar(id)}>
                                    {dialogo.btnCancel ?? "Não"}
                                </Botao>
                                <Botao {...PARAR_ESC_DO_MANTINE} cor={COLOR_MAP[tipo] ?? "blue"} className="h-11 sm:h-9 sm:min-w-24" onClick={() => onConfirmar(id)}>
                                    {dialogo.btnOk ?? "Sim"}
                                </Botao>
                            </div>
                        ) : (
                            <div className="mt-6 flex justify-center">
                                <Botao ref={botaoInicial} {...PARAR_ESC_DO_MANTINE} cor={COLOR_MAP[tipo] ?? "green"} className="h-11 w-full px-8 sm:h-9 sm:w-auto" onClick={() => onFechar(id)}>
                                    {dialogo.btnOk ?? "Ok"}
                                </Botao>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export const ConfirmModalProvider: React.FC<ConfirmModalContextProps> = ({ children }) => {
    const [dialogos, setDialogos] = useState<Dialogo[]>([]);
    const dialogosRef = useRef(dialogos);
    dialogosRef.current = dialogos;

    const show = useCallback((msg: string, onConfirm?: (() => void) | null, options?: ConfirmModalOptions | null, mode: ModoConfirmacao = "modal") => {
        const tipo: TipoConfirmacao = options?.tipo && ICON_MAP[options.tipo] ? options.tipo : "success";
        const novo: Dialogo = {
            id: proximoId++,
            mensagem: msg,
            onConfirm,
            tipo,
            modo: mode === "confirm" ? "confirm" : "modal",
            btnOk: options?.btnOk,
            btnCancel: options?.btnCancel,
            aberta: true,
        };
        setDialogos((actuais) => [...actuais, novo]);
    }, []);

    const hide = useCallback(() => {
        setDialogos((actuais) => actuais.map((d) => (d.aberta ? { ...d, aberta: false } : d)));
    }, []);

    const fechar = useCallback((id: number) => {
        setDialogos((actuais) => actuais.map((d) => (d.id === id ? { ...d, aberta: false } : d)));
    }, []);

    /** «Sim»: fecha e só depois corre o `onConfirm` (que pode abrir outro aviso). */
    const confirmar = useCallback((id: number) => {
        const dialogo = dialogosRef.current.find((d) => d.id === id);
        if (!dialogo?.aberta) {
            return;
        }
        fechar(id);
        dialogo.onConfirm?.();
    }, [fechar]);

    const saiu = useCallback((id: number) => {
        setDialogos((actuais) => actuais.filter((d) => d.id !== id || d.aberta));
    }, []);

    const valor = useMemo(() => ({ show, hide }), [show, hide]);

    return (
        <ConfirmModalContext.Provider value={valor}>
            {children}
            {dialogos.length > 0 && typeof document !== "undefined" && createPortal(
                <>
                    {dialogos.map((d) => (
                        <DialogoConfirmacao key={d.id} dialogo={d} onFechar={fechar} onConfirmar={confirmar} onSaiu={saiu} />
                    ))}
                </>,
                document.body,
            )}
        </ConfirmModalContext.Provider>
    );
};

export const useConfirmModal = () => {
    const context = useContext(ConfirmModalContext);
    if (!context) {
        throw new Error("useConfirmModal must be used within a ConfirmModalProvider");
    }
    return context;
};
